import React, { useState, useRef, useEffect } from 'react';
import {
  ChevronDown, ChevronUp, LogIn, LogOut, UserPlus, Trash2, ExternalLink, Search, SidebarClose, ShoppingCart, Gauge, BarChart3, Share2, UploadCloud, RefreshCw
} from 'lucide-react';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { useAuditDrawer } from '../audit/AuditDrawerProvider';

// --- NEW IMPORTS FOR SYNC ---
import { httpsCallable } from 'firebase/functions';
import { functionsInstance } from '../../firebase/firebase';

// YOU MUST ENSURE THIS IMPORT PATH IS CORRECT
import { runAudit } from '../../utils/audit';

// --- Constants ---
const TPT_PURCHASE_LINK =
  'https://www.teacherspayteachers.com/Product/TPT-Seller-SEO-Listing-Optimizer-App-Create-Titles-Description-Product-Links-13645689?utm_source=Mr%20Joel%20Site%20&utm_campaign=Mr%20Joel%20Site';
const MAX_TRIAL_GENERATIONS_DISPLAY = 10; // Default display value

/**
 * Sidebar component
 * - Discreet "Remove all" control + triple-check modal (checkbox + type DELETE + final confirm)
 * - No backend changes required: uses your existing handleDeleteTptProduct(id)
 * - Robust delete-all: loops until userTptProducts is *actually* empty
 */
const Sidebar = ({
  isOpen,
  setIsOpen,
  currentUser,
  userAccessData,
  isTrialActive,
  trialTimeRemaining,
  handleLogout,
  triggerAuthModal,
  userTptProducts,
  savedDescriptions,
  handleSelectTptProductForRewrite,
  handleLoadSavedDescription,
  handleDeleteTptProduct,     // <- used for all deletes
  handleDeleteSavedDescription,
  onBulkImportClick,
  onGoToSeo,
  onGoToSocial,
}) => {
  const [showMyTptProductsSection, setShowMyTptProductsSection] = useState(true);
  const [showSavedDescriptionsSection, setShowSavedDescriptionsSection] = useState(true);
  const [tptProductSearchTerm, setTptProductSearchTerm] = useState('');
  const [savedDescriptionSearchTerm, setSavedDescriptionSearchTerm] = useState('');

  // --- Modals State ---
  const [showBundleCreator, setShowBundleCreator] = useState(false);
  const [showKeywordResearch, setShowKeywordResearch] = useState(false);

  // Delete All (triple-check) modal state
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [ackIrreversible, setAckIrreversible] = useState(false);
  const [typedConfirm, setTypedConfirm] = useState('');
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [deleteTotal, setDeleteTotal] = useState(0);

  const { openAudit } = useAuditDrawer();

  // --- Ref to access fresh props in async delete handler ---
  const productsRef = useRef(userTptProducts);
  useEffect(() => {
    productsRef.current = userTptProducts;
  }, [userTptProducts]);
  // --- End ref ---

  // --- UPDATED SEARCH LOGIC: Includes Title, URL, and ID ---
  const filteredTptProducts = userTptProducts.filter((product) => {
    const term = tptProductSearchTerm.toLowerCase();
    return (
      product.productTitle?.toLowerCase().includes(term) ||
      product.tptUrl?.toLowerCase().includes(term) ||
      product.tptProductId?.toString().includes(term)
    );
  });

  const filteredSavedDescriptions = savedDescriptions.filter(
    (desc) =>
      desc.saveName?.toLowerCase().includes(savedDescriptionSearchTerm.toLowerCase()) ||
      desc.productName?.toLowerCase().includes(savedDescriptionSearchTerm.toLowerCase())
  );

  const handlePurchaseClick = (location) => {
    try {
      const analytics = getAnalytics();
      logEvent(analytics, 'purchase_click', { location });
    } catch (_) {}
  };

  // --- ANALYTICS HANDLER FOR GUIDES ---
  const handleGuideClick = (guideName) => {
    try {
      const analytics = getAnalytics();
      logEvent(analytics, 'guide_click', {
        content_type: 'how_to_guide',
        item_id: guideName,
      });
    } catch (_) {
      // Fails silently if analytics isn't set up
    }
  };
  // --- END NEW HANDLER ---

  // --- NEW: RESYNC HANDLER ---
  const handleResync = async (e, product) => {
    e.stopPropagation(); // Stop the row click

    // --- SHORT & CLEAR WARNING MESSAGE ---
    const confirmed = window.confirm(
      `🔄 Update "${product.productTitle}" from TPT?\n\n` +
      `✅ UPDATING: Title, Price & Original Description (Backend Source)\n` +
      `🛡️ SAFE: Your AI Drafts & SEO Rankings stay untouched.\n\n` +
      `Proceed?`
    );

    if (!confirmed) return;

    try {
      const resyncFn = httpsCallable(functionsInstance, 'resyncProductData');
      await resyncFn({ productId: product.id, tptUrl: product.tptUrl });
      alert("Success! Product data refreshed from TPT.\n\n(Note: Full AI Description updates coming soon!)");
    } catch (err) {
      console.error(err);
      alert("Error syncing: " + err.message);
    }
  };
  // --- END NEW RESYNC HANDLER ---

  const handleBulkImportClick = () => {
    if (onBulkImportClick) onBulkImportClick();
    if (window.innerWidth < 768) setIsOpen(false);
  };

  const openDeleteAll = () => {
    if (!userTptProducts?.length) return;
    setAckIrreversible(false);
    setTypedConfirm('');
    setDeleteTotal(userTptProducts.length);
    setShowDeleteAllModal(true);
  };

  /**
   * --- NEW ROBUST doDeleteAll function ---
   */
  const doDeleteAll = async () => {
    if (!ackIrreversible || typedConfirm !== 'DELETE') return;
    setIsDeletingAll(true);

    const idsToDelete = productsRef.current
      .map(p => p.id)
      .filter(Boolean);

    if (idsToDelete.length === 0) {
      setIsDeletingAll(false);
      setShowDeleteAllModal(false);
      return;
    }

    setDeleteTotal(idsToDelete.length);

    try {
      const analytics = getAnalytics();
      logEvent(analytics, 'delete_all_products_attempt', {
        initialCount: idsToDelete.length,
        source: 'sidebar_delete_all'
      });
    } catch (_) {}

    try {
      const deletePromises = [];

      for (const id of idsToDelete) {
        try {
          const deleteOperation = handleDeleteTptProduct(id, false);
          if (deleteOperation && typeof deleteOperation.then === 'function') {
            deletePromises.push(deleteOperation);
          }
        } catch (err) {
          console.error('Failed to *start* delete for', id, err);
        }
      }

      await Promise.all(deletePromises);

      if (deletePromises.length === 0 && idsToDelete.length > 0) {
        console.warn('handleDeleteTptProduct does not return a promise. Adding 1.5s delay to allow state to update.');
        await new Promise((r) => setTimeout(r, 1500));
      }

      setShowDeleteAllModal(false);

    } catch (err) {
      console.error('Delete all failed', err);
      alert('Something went wrong deleting all products. Please try again.');
    } finally {
      setIsDeletingAll(false);
    }
  };

  const handleOpenAudit = (product) => {
    try {
      const analytics = getAnalytics();
      logEvent(analytics, 'product_audit', {
        label: 'product audit',
        product_id: product.id,
        product_title: product.productTitle || '(untitled)',
        source: 'sidebar_product_row'
      });
    } catch (_) {}

    let auditData = product.audit;
    if (!auditData && runAudit) {
      console.warn("Audit data missing on product in sidebar. Running local fallback calculation.");
      auditData = runAudit(product, product.auditOverrides);
    }

    openAudit(product, auditData);
    if (window.innerWidth < 768) setIsOpen(false);
  };

  const handleRankClick = (product) => {
    onGoToSeo(product);
    if (window.innerWidth < 768) setIsOpen(false);
  };

  const handleSocialClick = (product) => {
    onGoToSocial(product);
    if (window.innerWidth < 768) setIsOpen(false);
  };

  return (
    <>
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-72 bg-slate-800 text-slate-100 p-6 space-y-6 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0 shadow-xl' : '-translate-x-full'
        } md:sticky md:top-0 md:h-screen md:translate-x-0 md:block md:w-80 lg:w-96 md:shadow-lg
        overflow-y-auto max-h-screen overscroll-contain`}
      >
        <button
          onClick={() => setIsOpen(false)}
          className="md:hidden absolute top-4 right-4 text-slate-300 hover:text-white z-40 p-1 rounded-full hover:bg-slate-700"
          aria-label="Close sidebar"
        >
          <SidebarClose size={24} />
        </button>

        <h2 className="text-2xl font-semibold text-sky-400 border-b border-sky-700 pb-2">My Resources</h2>

        <div className="my-4">
          <h3 className="text-lg font-medium text-slate-200">How to Use (Video Guides)</h3>
          <ul className="mt-2 space-y-2 text-sm text-slate-300 pl-1">
            <li>
              <a
                href="https://youtu.be/q2GyjPQVb28?si=vYYRmKfENuw_LL_B"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-sky-300 transition-colors"
                onClick={() => handleGuideClick('import_generate_guide')}
              >
                📦 Import &amp; Generate Descriptions
              </a>
            </li>
            <li>
              <a
                href="https://youtu.be/ZeYOWrIC8AI?si=3yPM3HVwNqBpmx0G"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-sky-300 transition-colors"
                onClick={() => handleGuideClick('seo_rank_tracker_guide')}
              >
                📊 SEO Rank Tracker
              </a>
            </li>
            <li>
              <a
                href="https://youtu.be/jM1UCwIp6zY?si=q-creJG6BtckWJEc"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-sky-300 transition-colors"
                onClick={() => handleGuideClick('social_media_guide')}
              >
                📣 Social Media Generator
              </a>
            </li>
            <li>
              <button
                type="button"
                onClick={() => {
                  setShowBundleCreator(true);
                  handleGuideClick('bundle_creator_tool');
                }}
                className="hover:text-sky-300 transition-colors"
              >
                🛠️ Bundle Creator Tool
              </button>
            </li>
            <li>
              <button
                type="button"
                onClick={() => {
                  setShowKeywordResearch(true);
                  handleGuideClick('keyword_research_tool');
                }}
                className="hover:text-sky-300 transition-colors flex items-center"
              >
                <Search size={14} className="mr-1.5 opacity-80" />
                Keyword Research Tool
              </button>
            </li>
          </ul>
        </div>

        <div className="my-4">
          <button
            type="button"
            onClick={() => setShowMyTptProductsSection(!showMyTptProductsSection)}
            className="flex justify-between items-center w-full py-2 text-lg font-medium text-slate-200 hover:text-sky-400 cursor-pointer focus:outline-none"
          >
            <span>My TPT Products</span>
            {showMyTptProductsSection ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>

        {showMyTptProductsSection && (
          <div className="mt-2 pl-2 space-y-2 text-sm">
            {!currentUser ? (
              <p className="text-slate-400 text-xs p-2 bg-slate-700 rounded-md">
                <LogIn size={14} className="inline mr-1" /> Please{' '}
                <button
                  onClick={() => triggerAuthModal('Log in to manage your TPT products.', 'login')}
                  className="underline"
                >
                  log in
                </button>{' '}
                or{' '}
                <button
                  onClick={() => triggerAuthModal('Sign up to manage your TPT products.')}
                  className="underline"
                >
                  sign up
                </button>{' '}
                to save and manage your TPT products here.
              </p>
            ) : (
              <>
                <div className="pb-3 mb-3 border-b border-slate-700">
                  <button
                    type="button"
                    onClick={handleBulkImportClick}
                    className="w-full bg-indigo-500 hover:bg-indigo-600 text-white font-semibold py-2 px-3 rounded-lg shadow-md flex items-center justify-center text-sm transition-colors"
                  >
                    <UploadCloud size={16} className="mr-2" />
                    Import All Products
                  </button>
                  <p className="text-xs text-slate-400 mt-2">Import all products from your TPT store URL.</p>
                </div>

                <p className="text-xs text-slate-400 mb-2">
                  Products added via URL. Click to load for rewriting. Rewritten descriptions are saved here.
                </p>

                <div className="flex items-center justify-between text-sm text-slate-300 mb-3">
                  <span>
                    Total Products:{' '}
                    <span className="font-bold text-sky-400">{userTptProducts.length}</span>
                  </span>

                  {currentUser && userTptProducts.length > 0 && (
                    <button
                      type="button"
                      onClick={openDeleteAll}
                      className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-red-300 hover:underline px-1 py-0.5 rounded focus:outline-none"
                      title="Remove all imported product links (irreversible)"
                      aria-label="Remove all imported product links"
                    >
                      <Trash2 size={12} className="opacity-70" />
                      Remove all
                    </button>
                  )}
                </div>
                {currentUser && userTptProducts.length > 0 && (
                  <p className="text-[11px] text-slate-500 -mt-2 mb-1">
                    Short note: permanently removes all imported links (can’t recover recent updates or rank snapshots).
                  </p>
                )}

                <div className="relative mb-3">
                  <input
                    type="search"
                    // UPDATED PLACEHOLDER
                    placeholder="Search by ID, title, or URL..."
                    value={tptProductSearchTerm}
                    onChange={(e) => setTptProductSearchTerm(e.target.value)}
                    className="w-full p-2 pl-8 border border-slate-600 rounded-md bg-slate-700 text-slate-200 placeholder-slate-400 focus:ring-1 focus:ring-sky-500 focus:border-sky-500 text-xs"
                  />
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                </div>

                {filteredTptProducts.length > 0 ? (
                  <ul className="divide-y divide-slate-700 max-h-60 overflow-y-auto custom-scrollbar">
                    {filteredTptProducts.map((product) => (
                      <li key={product.id} className="py-2 group border-b border-slate-700/50 last:border-0">
                        {/* Product Title */}
                        <button
                          type="button"
                          className="w-full text-left font-medium text-sky-400 truncate hover:underline focus:outline-none mb-1"
                          onClick={() => {
                            handleSelectTptProductForRewrite(product);
                            if (window.innerWidth < 768) setIsOpen(false);
                          }}
                          title={`Load: ${product.productTitle || 'Untitled Product'}`}
                        >
                          {product.productTitle || 'Untitled Product'}
                        </button>

                        <div className="flex justify-between items-center mt-1">
                          {/* LEFT SIDE: View, Audit, Rank, Social (WITH TEXT) */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <a
                              href={product.tptUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-slate-400 hover:text-sky-300 flex items-center"
                              title="View on TPT"
                            >
                              View <ExternalLink size={12} className="ml-1" />
                            </a>

                            <button
                              type="button"
                              onClick={() => handleOpenAudit(product)}
                              className="text-xs text-sky-300 hover:text-white flex items-center px-2 py-0.5 rounded-md bg-sky-700/40 hover:bg-sky-700 transition-colors"
                              title="Open Listing Audit"
                              aria-label="Open Listing Audit"
                            >
                              <Gauge size={12} className="mr-1" />
                              Audit
                            </button>

                            <button
                              type="button"
                              onClick={() => handleRankClick(product)}
                              className="text-xs text-green-300 hover:text-white flex items-center px-2 py-0.5 rounded-md bg-green-700/40 hover:bg-green-700 transition-colors"
                              title="Open SEO Rank Tracker"
                              aria-label="Open SEO Rank Tracker"
                            >
                              <BarChart3 size={12} className="mr-1" />
                              Rank
                            </button>

                            <button
                              type="button"
                              onClick={() => handleSocialClick(product)}
                              className="text-xs text-indigo-300 hover:text-white flex items-center px-2 py-0.5 rounded-md bg-indigo-700/40 hover:bg-indigo-700 transition-colors"
                              title="Open Social Media Planner"
                              aria-label="Open Social Media Planner"
                            >
                              <Share2 size={12} className="mr-1" />
                              Social
                            </button>
                          </div>

                          {/* RIGHT SIDE: Sync Icon + Delete Icon */}
                          <div className="flex items-center gap-1">
                            {/* --- SYNC BUTTON (ICON ONLY) --- */}
                            <button
                              type="button"
                              onClick={(e) => handleResync(e, product)}
                              className="text-blue-400 hover:text-white p-1 rounded hover:bg-blue-900/50 transition-colors"
                              title="Refresh Title & Price from TPT"
                            >
                              <RefreshCw size={14} />
                            </button>

                            {/* --- DELETE BUTTON --- */}
                            <button
                              type="button"
                              onClick={() => handleDeleteTptProduct(product.id)}
                              className="text-red-400 hover:text-red-300 opacity-70 group-hover:opacity-100 p-1 rounded hover:bg-red-900/50 transition-colors"
                              title="Delete product link from this app"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-slate-400 text-xs">
                    {userTptProducts.length > 0 && tptProductSearchTerm
                      ? 'No products match your search.'
                      : 'Add a TPT store URL to import products.'}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        </div>

        <div className="my-4">
          <button
            type="button"
            onClick={() => setShowSavedDescriptionsSection(!showSavedDescriptionsSection)}
            className="flex justify-between items-center w-full py-2 text-lg font-medium text-slate-200 hover:text-sky-400 cursor-pointer focus:outline-none"
          >
            <span>Saved Descriptions</span>
            {showSavedDescriptionsSection ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>

          {showSavedDescriptionsSection && (
            <div className="mt-2 pl-2 space-y-2 text-sm">
              {!currentUser ? (
                <p className="text-slate-400 text-xs p-2 bg-slate-700 rounded-md">
                  <LogIn size={14} className="inline mr-1" /> Please{' '}
                  <button onClick={() => triggerAuthModal('Log in to manage saved descriptions.', 'login')} className="underline">
                    log in
                  </button>{' '}
                  or{' '}
                  <button onClick={() => triggerAuthModal('Sign up to manage saved descriptions.')} className="underline">
                    sign up
                  </button>{' '}
                  to save and manage your description snapshots.
                </p>
              ) : (
                <>
                  <p className="text-xs text-slate-400 mb-2">
                    Snapshots of descriptions generated using the "Generate New Product Description Details" form. Click to load inputs and description.
                  </p>

                  <div className="relative mb-3">
                    <input
                      type="search"
                      placeholder="Search saved descriptions..."
                      value={savedDescriptionSearchTerm}
                      onChange={(e) => setSavedDescriptionSearchTerm(e.target.value)}
                      className="w-full p-2 pl-8 border border-slate-600 rounded-md bg-slate-700 text-slate-200 placeholder-slate-400 focus:ring-1 focus:ring-sky-500 focus:border-sky-500 text-xs"
                    />
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  </div>

                  {filteredSavedDescriptions.length > 0 ? (
                    <ul className="divide-y divide-slate-700 max-h-60 overflow-y-auto custom-scrollbar">
                      {filteredSavedDescriptions.map((desc) => (
                        <li key={desc.id} className="py-2 group">
                          <button
                            type="button"
                            className="w-full text-left font-medium text-sky-400 truncate hover:underline focus:outline-none"
                            onClick={() => {
                              handleLoadSavedDescription(desc);
                              if (window.innerWidth < 768) setIsOpen(false);
                            }}
                            title={`Load snapshot: ${desc.saveName}`}
                          >
                            {desc.saveName}
                          </button>
                          <div className="flex justify-between items-center mt-1">
                            <p className="text-xs text-slate-400">
                              {desc.savedAt?.toDate ? desc.savedAt.toDate().toLocaleDateString() : 'Date N/A'}
                            </p>
                            <button
                              type="button"
                              onClick={() => handleDeleteSavedDescription(desc.id, desc.saveName)}
                              className="text-xs text-red-400 hover:text-red-300 opacity-70 group-hover:opacity-100 p-1 rounded hover:bg-red-900/50"
                              title="Delete snapshot"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-slate-400 text-xs">
                      {savedDescriptions.length > 0 && savedDescriptionSearchTerm
                        ? 'No descriptions match your search.'
                        : 'No saved description snapshots yet.'}
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="mt-auto pt-6 border-t border-sky-700 text-xs">
          {currentUser ? (
            <>
              <p className="text-slate-400">
                User: <span className="font-medium text-slate-300">{currentUser?.email}</span>
              </p>

              {isTrialActive && !userAccessData.approvedByAdmin && userAccessData.trialEndsAt && userAccessData.trialEndsAt.toDate() > new Date() && (
                <p className="text-blue-400">{trialTimeRemaining}</p>
              )}

              {isTrialActive && !userAccessData.approvedByAdmin && (
                <p className="text-blue-400">
                  Generations left in trial:{' '}
                  {Math.max(0, MAX_TRIAL_GENERATIONS_DISPLAY - (userAccessData.trialGenerationsUsed || 0))}
                </p>
              )}

              {userAccessData?.approvedByAdmin && userAccessData.accessExpiresAt && (
                <p className="text-green-400">
                  Full access until: {userAccessData.accessExpiresAt.toDate().toLocaleDateString()}
                </p>
              )}
              {userAccessData?.approvedByAdmin && !userAccessData.accessExpiresAt && (
                <p className="text-green-400">Full access active.</p>
              )}

              {isTrialActive && !userAccessData.approvedByAdmin && (
                <a
                  href={TPT_PURCHASE_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handlePurchaseClick('sidebar_trial_button')}
                  className="mt-2 inline-block bg-green-500 hover:bg-green-600 text-white font-semibold py-1.5 px-3 rounded-md shadow-sm text-center w-full"
                  title="Purchase Full Access"
                >
                  <ShoppingCart size={14} className="inline mr-1" /> Purchase Full Access
                </a>
              )}

              <button
                onClick={handleLogout}
                className="mt-3 w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-1.5 px-3 rounded-md shadow-sm flex items-center justify-center"
              >
                <LogOut size={14} className="mr-1.5" /> Logout
              </button>
            </>
          ) : (
            <>
              <p className="text-slate-300 mb-2">Log in or sign up to save your work and access more features!</p>
              <button
                onClick={() => triggerAuthModal('Please log in to continue.', 'login')}
                className="w-full bg-sky-600 hover:bg-sky-700 text-white font-semibold py-1.5 px-3 rounded-md shadow-sm flex items-center justify-center mb-2"
              >
                <LogIn size={14} className="mr-1.5" /> Login
              </button>
              <button
                onClick={() => triggerAuthModal('Sign up for a free trial to continue.')}
                className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-1.5 px-3 rounded-md shadow-sm flex items-center justify-center"
              >
                <UserPlus size={14} className="mr-1.5" /> Sign Up (Free Trial)
              </button>
            </>
          )}
        </div>
      </aside>

      {/* --- MODALS ARE OUTSIDE <aside> --- */}

      {/* --- BUNDLE CREATOR MODAL --- */}
      {showBundleCreator && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShowBundleCreator(false)}
        >
          <div
            className="w-full max-w-5xl h-[90vh] rounded-xl bg-slate-800 border border-slate-700 shadow-2xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b border-slate-700 flex-shrink-0">
              <h3 className="text-lg font-semibold text-sky-400">
                Bundle Creator Tool
              </h3>
              <button
                type="button"
                onClick={() => setShowBundleCreator(false)}
                className="text-slate-300 hover:text-white p-1 rounded-full hover:bg-slate-700"
                aria-label="Close modal"
              >
                <SidebarClose size={24} />
              </button>
            </div>
            <div className="flex-1 p-0 md:p-4 overflow-hidden">
              <iframe
                src="https://scribehow.com/embed/How_To_Use_The_Bundles_Creation_Tool__4rN3W8aLQZuwD4dy3SFEuw?as=scrollable"
                width="100%"
                height="100%"
                allow="fullscreen"
                style={{ border: 0, minHeight: '640px' }}
                title="Bundle Creator Tool"
              ></iframe>
            </div>
          </div>
        </div>
      )}

      {/* --- KEYWORD RESEARCH MODAL --- */}
      {showKeywordResearch && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShowKeywordResearch(false)}
        >
          <div
            className="w-full max-w-5xl h-[90vh] rounded-xl bg-slate-800 border border-slate-700 shadow-2xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b border-slate-700 flex-shrink-0">
              <h3 className="text-lg font-semibold text-sky-400">
                Keyword Research Tool
              </h3>
              <button
                type="button"
                onClick={() => setShowKeywordResearch(false)}
                className="text-slate-300 hover:text-white p-1 rounded-full hover:bg-slate-700"
                aria-label="Close modal"
              >
                <SidebarClose size={24} />
              </button>
            </div>
            <div className="flex-1 p-0 md:p-4 overflow-hidden">
              <iframe
                src="https://scribehow.com/embed/How_to_use_the_Keyword_Research_Tool__SN7UmJi2TZWYxt_hOhwuwg?as=scrollable"
                width="100%"
                height="100%"
                allow="fullscreen"
                style={{ border: 0, minHeight: '640px' }}
                title="Keyword Research Tool"
              ></iframe>
            </div>
          </div>
        </div>
      )}

      {/* Triple-check Delete All Modal */}
      {showDeleteAllModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-xl bg-slate-900 border border-slate-700 p-5 shadow-2xl">
            <h3 className="text-lg font-semibold text-slate-100 mb-2">
              Delete ALL Imported Products?
            </h3>
            <p className="text-sm text-slate-300 mb-3">
              This action will remove <span className="font-semibold text-sky-300">{deleteTotal}</span> product links from your account.
            </p>
            <ul className="text-xs text-slate-400 space-y-1 mb-4 list-disc pl-5">
              <li>Recent updates to listings (stored in-app) will be lost.</li>
              <li>Local audit overrides &amp; scores will be lost.</li>
              <li>Stored SEO rank snapshots/history in this app will be lost.</li>
              <li>This cannot be undone.</li>
            </ul>

            <label className="flex items-start gap-2 text-sm text-slate-200 mb-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1"
                checked={ackIrreversible}
                onChange={(e) => setAckIrreversible(e.target.checked)}
              />
              <span>I understand this is permanent and I won’t be able to recover my recent updates or rankings.</span>
            </label>

            <div className="mb-4">
              <label className="block text-xs text-slate-300 mb-1">
                Type <span className="font-mono px-1 py-0.5 rounded bg-slate-800 border border-slate-700">DELETE</span> to confirm:
              </label>
              <input
                value={typedConfirm}
                onChange={(e) => setTypedConfirm(e.target.value)}
                className="w-full p-2 rounded-md bg-slate-800 border border-slate-600 text-slate-100 text-sm focus:ring-1 focus:ring-red-500 focus:border-red-500"
                placeholder="DELETE"
                autoFocus
              />
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowDeleteAllModal(false)}
                className="px-3 py-1.5 rounded-md text-sm bg-slate-700 hover:bg-slate-600 text-slate-100"
                disabled={isDeletingAll}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doDeleteAll}
                disabled={!ackIrreversible || typedConfirm !== 'DELETE' || isDeletingAll}
                className={`px-3 py-1.5 rounded-md text-sm font-semibold flex items-center ${
                  !ackIrreversible || typedConfirm !== 'DELETE' || isDeletingAll
                    ? 'bg-red-900/50 text-red-300 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
                title="This permanently deletes all imported products"
              >
                {isDeletingAll ? 'Deleting…' : (<><Trash2 size={14} className="mr-1" /> Confirm Delete All</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;